import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

//UI
import { Box, Button, Checkbox, Flex, HStack, Heading, Text } from '@chakra-ui/react'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import VendorDeclineUploadModal from '../../components/Modal/VendorDeclineUploadModal'

//Hooks

import { authSelector } from '@/stores/auth/authSlice'
import { CostBudgetType, SourcingPostType } from '@eapproval/offer-shared'
import { useTranslation } from 'react-i18next'
import VendorUploadGeneralInfoSection from './VendorUploadGeneralInfoSection'
import VendorUploadQuotationDetailSection from './VendorUploadQuotationDetailSection'
import VendorUploadQuotationInfoSection from './VendorUploadQuotationInfoSection'
import VendorUploadTenderDocumentSection from './VendorUploadTenderDocumentSection'
import { createDefaultQuotationDetailRow } from './VendorUploadPage.helper'

// Types
import { ErrorSection, LoadingSection } from '@/components/Elements'
import config, { APP_MODE } from '@/config'
import useOfferCostBudget from '@/features/offerCostBudgets/useOfferCostBudget'
import useZZZ6 from '@/features/zzz6/useZZZ6'
import { isNullish } from '@/utils/helper'
import { roundNumber } from '@/utils/stringHelper'
import VendorUploadFrameAgreementSection from './VendorUploadFrameAgreementSection'
import VendorUploadQuotationPageHeader from './VendorUploadQuotationPageHeader'

const VendorUploadQuotationPage = () => {
  const { costBudgetId } = useParams()

  const { user } = useSelector(authSelector)
  const isProduction = config.mode == APP_MODE.PRODUCTION

  //Local State
  const [confirmSubmit, setConfirmSubmit] = useState(false)
  const [lastRequestType, setLastRequestType] = useState(SourcingPostType.REQUEST_FOR_QUOTATION)
  const [vendorDeclineModalDetail, setVendorDeclineModalDetail] = useState({
    show: false,
  })

  //Hooks
  const dispatch = useDispatch()
  const methods = useForm()
  const { t, i18n } = useTranslation()
  const { useFetchZZZ6s } = useZZZ6()

  const navigate = useNavigate()
  const { useGetRFQDetail, useVendorUploadQuotation, useVendorUploadFrameAgreement } = useOfferCostBudget()

  const { data: RFQDetail, isLoading } = useGetRFQDetail({ costBudgetId })
  const { data: zzz6Data } = useFetchZZZ6s({
    page: 1,
    pageSize: 100000,
    filters: { status: 'active' },
  })

  const vendorUploadCostBudgetQuotation = useVendorUploadQuotation()
  const vendorUploadFrameAgreement = useVendorUploadFrameAgreement()

  //Translated

  const vendorStatus = RFQDetail?.status ?? ''
  const canUpload = RFQDetail?.canUpload ?? false
  const canReject = RFQDetail?.canReject ?? false

  const costBudgetType = RFQDetail?.costBudgetType

  const tenderDocuments = RFQDetail?.tenderDocuments ?? []

  useEffect(() => {
    if (RFQDetail) {
      const { projectStartDate, projectEndDate, submissionDueDate, ...rest } = RFQDetail

      const projectDate = `${moment(projectStartDate || '').format('DD-MMM-YYYY')} to ${moment(projectEndDate).format('DD-MMM-YYYY')}`
      const submissionDueDateString = submissionDueDate ? moment(submissionDueDate).format('DD-MMM-YYYY') : '(Missing)'

      const tenderDocuments = Array.isArray(rest.tenderDocuments) ? rest.tenderDocuments : []
      const latestTenderDocument = tenderDocuments[tenderDocuments.length - 1]

      const internalLastRequestType = Array.isArray(RFQDetail?.vendorActionHistory)
        ? (RFQDetail.vendorActionHistory
            .slice()
            .reverse()
            .find((his) => Object.values(SourcingPostType).includes(his.historyType))?.historyType ??
          SourcingPostType.REQUEST_FOR_QUOTATION)
        : SourcingPostType.REQUEST_FOR_QUOTATION

      setLastRequestType(RFQDetail?.currentRequestType ?? SourcingPostType.REQUEST_FOR_QUOTATION)

      //Default Value for
      const insurance =
        !latestTenderDocument?.quotationTemplate?.insurance || latestTenderDocument?.quotationTemplate?.insurance === ''
          ? 'EC'
          : latestTenderDocument?.quotationTemplate?.insurance

      let incoterm = 'CIF HK'

      if (
        latestTenderDocument?.quotationTemplate?.incoterm &&
        latestTenderDocument?.quotationTemplate?.incoterm !== ''
      ) {
        incoterm = latestTenderDocument?.quotationTemplate?.incoterm
      } else if (rest.countryOfInstallation === 'mo') {
        incoterm = 'CIF Macau'
      }

      let paymentTerm =
        RFQDetail?.costBudgetType === CostBudgetType.SUPPLY_MATERIAL_ONLY
          ? '100% payment after delivery'
          : 'Monthly progress payment'

      methods.reset({
        ...rest,
        vendorRemark: '',
        projectDate,
        projectStartDate,
        projectEndDate,
        submissionDueDate: submissionDueDateString,
        //find the later date between projectEndDate and 180 days later
        validDate: moment(projectEndDate).isAfter(moment().add(180, 'day'))
          ? projectEndDate
          : moment().add(180, 'day').toDate(),
        discountAmount: 0,
        discountRate: 0,
        currency: latestTenderDocument?.quotationTemplate?.currency ?? 'HKD',
        quotationDetailTemplate: latestTenderDocument?.quotationTemplate?.details,
        quotationDetails: latestTenderDocument?.quotationTemplate?.details,
        DLP: latestTenderDocument?.quotationTemplate?.DLP,
        leadTime: latestTenderDocument?.quotationTemplate?.leadTime,
        insurance: insurance,
        incoterm: incoterm,
        warranty: latestTenderDocument?.quotationTemplate?.warranty,
        paymentTerm: paymentTerm,
      })

      if (
        (internalLastRequestType === SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT ||
          internalLastRequestType === SourcingPostType.REQUEST_FOR_UPDATE_OF_TERMS ||
          internalLastRequestType === SourcingPostType.REQUEST_FOR_CLARIFICATION ||
          internalLastRequestType === SourcingPostType.REQUEST_FOR_DETAILED_TABLE ||
          !canUpload) &&
        RFQDetail?.lastUpload
      ) {
        let defaultQuotationDetails = Array.isArray(RFQDetail?.lastUpload?.quotationDetails)
          ? RFQDetail.lastUpload.quotationDetails
          : []

        if (
          internalLastRequestType === SourcingPostType.REQUEST_FOR_DETAILED_TABLE &&
          defaultQuotationDetails.length === 0
        ) {
          const rowCostBudgetType = RFQDetail?.type || RFQDetail?.costBudgetType
          defaultQuotationDetails = [createDefaultQuotationDetailRow(rowCostBudgetType, zzz6Data?.data ?? [])]
        }

        methods.setValue('quotationDetails', defaultQuotationDetails)
        methods.setValue('DLP', RFQDetail?.lastUpload?.DLP)

        if (RFQDetail?.lastUpload?.insurance) {
          methods.setValue('insurance', RFQDetail?.lastUpload?.insurance)
        }

        if (RFQDetail?.lastUpload?.incoterm) {
          methods.setValue('incoterm', RFQDetail?.lastUpload?.incoterm)
        }
        if (RFQDetail?.lastUpload?.leadTime) {
          methods.setValue('leadTime', RFQDetail?.lastUpload?.leadTime)
        }

        methods.setValue('warranty', RFQDetail?.lastUpload?.warranty)
        methods.setValue('amount', RFQDetail?.lastUpload?.budget)
        methods.setValue('discountAmount', RFQDetail?.lastUpload?.discountAmount)
      }

      // if (
      //   Array.isArray(RFQDetail?.lastUpload?.frameAgreementDetails) &&
      //   RFQDetail?.lastUpload?.frameAgreementDetails.length > 0
      // ) {
      //   methods.setValue('frameAgreementDetails', RFQDetail?.lastUpload?.frameAgreementDetails)
      // }

      if (Array.isArray(RFQDetail?.referenceFrameAgreements) && RFQDetail?.referenceFrameAgreements.length > 0) {
        methods.setValue('referenceFrameAgreements', RFQDetail?.referenceFrameAgreements)
      }
    }
  }, [RFQDetail, methods])

  const onSubmit = async (data) => {
    //Handle multiple file upload

    if (!confirmSubmit) {
      toast.error('Please check the box above the “Submit“ button to proceed your Quotation submission !')
      return
    }

    if (costBudgetType === CostBudgetType.FRAME_AGREEMENT) {
      //No need to do further checking
      // console.log('data', data)

      await vendorUploadFrameAgreement.mutateAsync({
        costBudgetId: costBudgetId,
        // validDate: data.validDate,
        frameAgreements: data.frameAgreements.map((file) => file?._id || '').filter((id) => id !== ''),
        remark: data.vendorRemark,
      })
      return
    }

    if (!data?.amount) {
      toast.error('Missing Amount !')
      return
    }
    if (!data?.validDate) {
      toast.error('Missing Valid Date !')
      return
    }
    // if (!data?.creditTerm) {
    //   toast.error('Missing Credit Term !')
    //   return
    // }
    if (!data?.paymentTerm) {
      toast.error('Missing Payment Term !')
      return
    }

    if (!Array.isArray(data?.quotationDetails)) {
      toast.error('[Unexpected Error] Missing quotation details')
      return
    }

    const quotationDetailTotalPrice = roundNumber(
      data.quotationDetails.reduce((acc, cur) => acc + Number(cur?.totalPrice || 0), 0),
      2
    )
    const expectedAmount = roundNumber(quotationDetailTotalPrice - Number(data.discountAmount || 0), 2)
    const submittedAmount = roundNumber(Number(data.amount || 0), 2)

    if (quotationDetailTotalPrice > 0 && submittedAmount !== expectedAmount) {
      console.log(data.amount)
      console.log(data.discountAmount)
      console.log(data.quotationDetails)
      toast.error(`The quotation amount do not match ${submittedAmount} vs ${expectedAmount}`)
      return
    }

    if (
      lastRequestType === SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT &&
      (!data?.technicalSupportDocuments ||
        (Array.isArray(data?.technicalSupportDocuments) && data.technicalSupportDocuments.length === 0))
    ) {
      toast.error('Please upload technical support documents ')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const res = await vendorUploadCostBudgetQuotation.mutateAsync({
        costBudgetId: costBudgetId,
        amount: data.amount,
        validDate: data.validDate,
        files: Array.isArray(data.quotationFiles)
          ? data.quotationFiles.map((file) => file?._id || '').filter((id) => id !== '')
          : [],
        technicalSupportDocuments: Array.isArray(data.technicalSupportDocuments)
          ? data.technicalSupportDocuments.map((file) => file?._id || '').filter((id) => id !== '')
          : [],
        creditTerm: data.creditTerm,
        paymentTerm: data.paymentTerm,
        remark: data.vendorRemark,
        discountAmount: data.discountAmount,
        discountRate: data.discountRate,
        currency: data.currency,
        quotationDetails: data.quotationDetails.map((row) => {
          return {
            ...row,
            discount: isNullish(row?.discount) || row.discount === '' ? 0 : row.discount,
          }
        }),
        DLP: data.DLP,
        leadTime: data.leadTime,
        insurance: data.insurance,
        incoterm: data.incoterm,
        warranty: data.warranty,
      })
      methods.reset({ files: null, amount: 0, vendorRemark: '' })
      toast.info(t('VENDOR_UPLOAD_PAGE_MESSAGE_SUCCESS_UPLOAD'))
      navigate(isProduction ? '/RFQs' : '/external/RFQs')
      // document.getElementById('files').value = ''
    } catch (err) {
      toast.error(err)
      console.log(data)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleRejectVendorQuotation = async () => {
    setVendorDeclineModalDetail({
      show: true,
      costBudgetId: costBudgetId,
    })
  }

  if (!costBudgetId || costBudgetId === '') {
    return <ErrorSection text="Missing Offer Id" />
  }

  if (isLoading) {
    return <LoadingSection />
  }

  if (RFQDetail?.vendorId !== user.supplier) {
    return (
      <Flex w="100%" mb="auto" p="6" direction="column" alignItems={'center'} gap="5">
        <Heading> {t('VENDOR_UPLOAD_PAGE_PAGE_NOT_ALLOW_WARNING')} </Heading>
        <Box>
          <Button
            colorScheme="teal"
            onClick={() =>
              navigate(isProduction ? '/subcontractors/quotations' : '/external/subcontractors/quotations')
            }
          >
            {t('VENDOR_UPLOAD_PAGE_GO_TO_MY_LIST_OF_QUOTATIONS')}
          </Button>
        </Box>
      </Flex>
    )
  }

  return (
    <>
      <VendorDeclineUploadModal modalDetail={vendorDeclineModalDetail} setModalDetail={setVendorDeclineModalDetail} />

      <Box
        shadow="md"
        borderWidth="1px"
        bg="white"
        w={{ base: '100%', md: '100%' }}
        mb="auto"
        h="90%"
        overflow={'scroll'}
      >
        <VendorUploadQuotationPageHeader RFQDetail={RFQDetail} lastRequestType={lastRequestType} />
        <Box p={5}>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <Flex gap={3} direction={'column'}>
                <VendorUploadGeneralInfoSection costBudgetType={costBudgetType} />

                <VendorUploadTenderDocumentSection
                  tenderDocuments={tenderDocuments}
                  history={RFQDetail?.vendorActionHistory ?? []}
                  canUpload={canUpload}
                />

                {costBudgetType !== CostBudgetType.FRAME_AGREEMENT && (
                  <>
                    <Flex p="5" borderRadius={15} border="1px solid rgb(226, 232, 240)" gap={3} direction={'column'}>
                      <VendorUploadQuotationInfoSection
                        canUpload={canUpload}
                        requestType={lastRequestType}
                        costBudgetType={RFQDetail?.type}
                      />
                    </Flex>
                    {[
                      SourcingPostType.REQUEST_FOR_QUOTATION,
                      SourcingPostType.REQUEST_FOR_CLARIFICATION,
                      SourcingPostType.REQUEST_FOR_DETAILED_TABLE,
                    ].includes(lastRequestType) && (
                      <Flex p="5" borderRadius={15} border="1px solid rgb(226, 232, 240)" gap={3} direction={'column'}>
                        <VendorUploadQuotationDetailSection canUpload={canUpload} RFQDetail={RFQDetail} />
                      </Flex>
                    )}
                  </>
                )}

                {costBudgetType === CostBudgetType.FRAME_AGREEMENT && (
                  <VendorUploadFrameAgreementSection canUpload={canUpload} requestType={lastRequestType} />
                )}
              </Flex>

              {canUpload && (
                <>
                  <Flex mt="5">
                    <Text color={'black.300'} as="b">
                      {t('VENDOR_UPLOAD_PAGE_NOT_JOIN_BID_AGREEMENT_TITLE')}
                    </Text>
                  </Flex>
                  <Flex mt="5">
                    <Checkbox
                      w={'25%'}
                      p="5"
                      borderColor={'gray.400'}
                      onChange={(e) => {
                        setConfirmSubmit(e.target.checked)
                      }}
                    />

                    <Text color={'blue.300'} as="i">
                      {t('VENDOR_UPLOAD_PAGE_NOT_JOIN_BID_AGREEMENT')}
                    </Text>
                  </Flex>
                </>
              )}

              <HStack my="2">
                {canUpload && (
                  <Button colorScheme="blue" type="submit">
                    {t('VENDOR_UPLOAD_PAGE_SUBMIT')}
                  </Button>
                )}

                {canReject && (
                  <Button colorScheme="red" onClick={handleRejectVendorQuotation}>
                    {t('VENDOR_UPLOAD_PAGE_REJECT')}
                  </Button>
                )}
              </HStack>
            </form>
          </FormProvider>
        </Box>
      </Box>
    </>
  )
}

export default VendorUploadQuotationPage
